import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconDownload, IconSearch, IconX } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { getOverdueAccount } from "api/debtor.service";
import { navigateTo } from "common/navigation";
import { getSeverity } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import React, { useEffect, useMemo, useState } from "react";
import { OverdueDebtors } from "types/model.types";

const AdminOverdueAccountReportPage: React.FC = () => {
  const [debtors, setDebtors] = useState<OverdueDebtors>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activePage, setActivePage] = useState(1);
  // Inside your component:
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const statusOptions = [
    { value: "3", label: "3+ Months Overdue" },
    { value: "2", label: "2 Months Overdue" },
    { value: "1", label: "1 Month Overdue" },
  ];
  // const getSeverity = (debtor: OverdueDebtor): number => {
  //   if (debtor.month3 > 0) return 3;
  //   if (debtor.month2 > 0) return 2;
  //   if (debtor.month1 > 0) return 1;
  //   return 0;
  // };

  const fetchDebtors = async (downloadCsv = false) => {
    try {
      const result = await safeApiCall(getOverdueAccount(downloadCsv));
      if (!downloadCsv) setDebtors(result);
    } catch (err) {
      setError("Failed to fetch overdue accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    fetchDebtors();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    const normalizedSearch = (searchTerm ?? "").trim().toLowerCase();

    return debtors.filter((b) => {
      const name = b.name?.toLowerCase() ?? "";
      const alpha = b.alpha?.toLowerCase() ?? "";

      let matchesSearch = true;
      if (normalizedSearch.length >= 3) {
        matchesSearch =
          name.includes(normalizedSearch) ||
          alpha.includes(normalizedSearch) ||
          name.startsWith(normalizedSearch) ||
          alpha.startsWith(normalizedSearch);
      }
      let matchesStatus = true;
      if (statusFilter) {
        const severity = getSeverity(b);
        matchesStatus =
          statusFilter === null || severity.toString() === statusFilter;
      }

      return matchesSearch && matchesStatus;
    });
    //.sort((a, b) => getSeverity(b) - getSeverity(a)); // Descending
  }, [debtors, searchTerm, statusFilter]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  const totals = useMemo(() => {
    return filteredItems.reduce(
      (acc, d) => {
        acc.opening += Number(d.opening ?? 0);
        acc.balance += Number(d.balance ?? 0);
        acc.currentMonth += Number(d.currentMonth ?? 0);
        acc.month1 += Number(d.month1 ?? 0);
        acc.month2 += Number(d.month2 ?? 0);
        acc.month3 += Number(d.month3 ?? 0);
        return acc;
      },
      {
        opening: 0,
        balance: 0,
        currentMonth: 0,
        month1: 0,
        month2: 0,
        month3: 0,
      },
    );
  }, [filteredItems]);

  const rows = paginatedItems.map((debtor) => {
    let overdueLabel = "";

    if (debtor.month3 > 0) {
      overdueLabel = "3+ Months Overdue";
    } else if (debtor.month2 > 0) {
      overdueLabel = "2 Months Overdue";
    } else {
      overdueLabel = "Current";
    }

    return (
      <Table.Tr key={debtor.id}>
        <Table.Td>{debtor.id}</Table.Td>
        <Table.Td>{debtor.name}</Table.Td>
        <Table.Td>{debtor.opening.toFixed(2)}</Table.Td>
        <Table.Td>{debtor.balance.toFixed(2)}</Table.Td>
        <Table.Td>{debtor.currentMonth.toFixed(2)}</Table.Td>
        <Table.Td>{debtor.month1.toFixed(2)}</Table.Td>
        <Table.Td>{debtor.month2.toFixed(2)}</Table.Td>
        <Table.Td>{debtor.month3.toFixed(2)}</Table.Td>
        <Table.Td>
          <Badge
            color={
              debtor.month3 > 0
                ? "red"
                : debtor.month2 > 0
                  ? "orange"
                  : debtor.month1 > 0
                    ? "yellow"
                    : debtor.currentMonth > 0
                      ? "blue"
                      : "gray"
            }
          >
            {overdueLabel}
          </Badge>
        </Table.Td>
      </Table.Tr>
    );
  });
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
    setActivePage(1); // Reset pagination to first page
  };

  return (
    <Stack>
      <Group justify="space-between" align="end" gap="sm">
        <Title order={2}>Overdue Accounts</Title>
        <Button
          onClick={() => {
            navigateTo(
              `/print/overdue-accounts?s=${searchTerm ?? ""}&f=${statusFilter === "" || statusFilter === null ? "" : statusFilter}`,
            );
          }}
          variant="gradient"
        >
          Print
        </Button>
      </Group>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : error ? (
        <Alert color="red">{error}</Alert>
      ) : (
        <>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Text size="sm" c="dimmed">
              Showing 30 items per page
            </Text>
            <Pagination
              total={Math.ceil((filteredItems.length ?? 0) / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
          <Group
            justify="flex-end"
            align="flex-start"
            wrap="nowrap"
            mb="md"
            w="100%"
          >
            <TextInput
              placeholder="Search by any title"
              mb="md"
              leftSection={<IconSearch size={16} stroke={1.5} />}
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ flex: 1 }}
              rightSection={
                searchTerm.length > 0 ? (
                  <ActionIcon onClick={() => setSearchTerm("")} size="sm">
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
            />

            <Tooltip label="Download CSV">
              <ActionIcon size={36} onClick={() => fetchDebtors(true)}>
                <IconDownload size={24} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Table striped highlightOnHover withTableBorder w={"100%"}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Opening</Table.Th>
                <Table.Th>Balance</Table.Th>
                <Table.Th>Current</Table.Th>
                <Table.Th>Month 1</Table.Th>
                <Table.Th>Month 2</Table.Th>
                <Table.Th>Month 3</Table.Th>
                <Table.Th>
                  <Select
                    placeholder="All"
                    data={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    clearable
                    mb="md"
                  />
                </Table.Th>
              </Table.Tr>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>
                  <strong>Totals</strong>
                </Table.Th>
                <Table.Th>{totals.opening.toFixed(2)}</Table.Th>
                <Table.Th>{totals.balance.toFixed(2)}</Table.Th>
                <Table.Th>{totals.currentMonth.toFixed(2)}</Table.Th>
                <Table.Th>{totals.month1.toFixed(2)}</Table.Th>
                <Table.Th>{totals.month2.toFixed(2)}</Table.Th>
                <Table.Th>{totals.month3.toFixed(2)}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil((filteredItems.length ?? 0) / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
    </Stack>
  );
};

export default AdminOverdueAccountReportPage;
